/**
 * Round 95s (Jun 2026) — ADR Tax Drag banner for the
 * "Income by Portfolio" card.
 *
 * Sits directly UNDER the per-portfolio
 * IncomeConcentrationBanner so users see the foreign-withholding
 * hit on this portfolio's ADR slice without clicking into a
 * single stock. Self-hides when the portfolio holds no ADRs with
 * a known WHT rate, OR when the gross is $0.
 *
 * Tone: amber-on-amber (between the red concentration banner and
 * the green health pills) so it reads as "informational, not
 * alarming" — most users CAN recover most of the WHT via the
 * Foreign Tax Credit on Form 1116.
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { Globe, BookOpen } from 'lucide-react';
import { ccToFlag, ccName } from '@/lib/flagEmoji';
import AbbrTip from '@/components/AbbrTip';

function _money(n) {
  if (n == null || !isFinite(n)) return '$0';
  return `$${Number(n).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
}

export default function AdrTaxDragBanner({ taxDrag, onStockClick }) {
  if (!taxDrag) return null;
  const gross = Number(taxDrag.gross_annual || 0);
  const withheld = Number(taxDrag.withheld_annual || 0);
  const ftc = Number(taxDrag.recoverable_ftc || 0);
  if (gross <= 0 || withheld <= 0) return null;

  const tickers = Array.isArray(taxDrag.tickers) ? taxDrag.tickers : [];
  // Show up to 4 unique country flags inline; the rest collapse.
  const uniqueCcs = Array.from(new Set(tickers.map((t) => t.country_code))).slice(0, 4);

  return (
    <div
      className="rounded-md border border-amber-300/60 bg-amber-50/60 dark:border-amber-700/40 dark:bg-amber-950/30 p-3 text-sm"
      data-testid="contrast-row-adr-tax-drag-banner"
    >
      <div className="flex items-center gap-2 text-amber-800 dark:text-amber-200 font-semibold mb-1">
        <Globe className="w-4 h-4 flex-shrink-0" />
        <span>ADR Tax Drag</span>
        <Link
          to="/glossary#entry-adr-tax-drag"
          className="inline-flex items-center gap-1 text-[10px] font-normal text-amber-700/90 dark:text-amber-300/90 hover:text-amber-900 dark:hover:text-amber-100 underline underline-offset-2 decoration-dotted hover:decoration-solid"
          title="Open the ADR Tax Drag glossary entry"
          data-testid="adr-tax-drag-glossary-link"
        >
          <BookOpen className="w-3 h-3" />
          Glossary
        </Link>
        <span className="ml-auto inline-flex items-center gap-1 text-xs font-normal text-amber-700/80 dark:text-amber-300/80">
          {uniqueCcs.map((cc) => (
            <span key={cc} title={ccName(cc)} className="text-base leading-none">{ccToFlag(cc)}</span>
          ))}
          {taxDrag.adr_count > 4 && (
            <span className="text-[10px]">+{taxDrag.adr_count - 4} more</span>
          )}
        </span>
      </div>
      <div className="text-amber-900/90 dark:text-amber-100/90 leading-relaxed">
        Foreign <AbbrTip term="WHT">withholding</AbbrTip> on your {taxDrag.adr_count}
        {taxDrag.adr_count === 1 ? ' ADR' : ' ADRs'} cost you{' '}
        <strong>~{_money(withheld)}/yr</strong>{' '}
        — up to <strong>~{_money(ftc)}</strong> is recoverable as a{' '}
        <AbbrTip term="FTC">Foreign Tax Credit</AbbrTip>{' '}
        on Form 1116. Click any ADR ticker to see the per-share math.
      </div>
      {tickers.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-2">
          {tickers.slice(0, 8).map((t) => (
            <button
              key={t.ticker}
              type="button"
              onClick={() => onStockClick?.(t.ticker)}
              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-mono font-bold bg-amber-100 hover:bg-amber-200 dark:bg-amber-900/50 dark:hover:bg-amber-900/80 text-amber-900 dark:text-amber-100 border border-amber-300/60 dark:border-amber-700/60 transition"
              data-testid={`adr-tax-drag-chip-${t.ticker}`}
              title={`${t.ticker} · ${ccName(t.country_code)} · ${t.rate_pct}% WHT · −${_money(t.withheld_annual)}/yr`}
            >
              <span className="text-xs leading-none">{ccToFlag(t.country_code)}</span>
              {t.ticker}
              <span className="opacity-70">−{_money(t.withheld_annual)}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
